import {
  Input,
  Button,
  DateRangePicker,
  Spacer,
  Chip,
} from '@nextui-org/react';
import { useState, ChangeEvent, useEffect } from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import ImageSelection from 'silal_app_base_react/src/components/image_selection';
import { S3ApiRepository } from 'silal_app_base_react/src/data/repositories/s3_repository';
import { managementAxiosInstance } from 'silal_app_base_react/src/utils/axios/axios';
import { ImageType } from 'silal_app_base_react/src/data/types/files';
import ShowImage from 'silal_app_base_react/src/components/show_image';
import {
  CURRENT_CURRENCY,
  ISRAEL_TIMEZONE,
} from 'silal_app_base_react/src/config/constants';
import PromotionsRepository from 'data/repositories/promotions_repository';
import { SellerFilesRepository } from 'data/repositories/files_repository';
import { useSelector } from 'react-redux';
import { getCurrentStore } from 'core/hooks/use_selector';
import { storeCoverPlaceholder_path } from 'assets/index';
import { Modal, Table } from 'antd';
import { ListingItem } from 'silal_app_base_react/src/data/types/listings';
import { parseAbsoluteToLocal } from '@internationalized/date';
import moment from 'moment';
import { formatPrice } from 'silal_app_base_react/src/utils/functions/formatting_functions';

interface PromotionCreation {
  name_en: string;
  name_ar: string;
  discount_type: string | null;
  discount_value: string;
  min_order_value: number | null;
  max_discount_amount: number | null;
  start_datetime: string | null;
  end_datetime: string | null;
  item_ids: number[] | undefined;
}

interface CreatePromotionModalProps {
  promotionVisibilityAndItems: { show: boolean; items: ListingItem[] };
  initiallySelectedItems?: ListingItem[];
  onDone?: () => void;
  onCancel?: () => void;
}

interface ValidationErrors {
  [key: string]: string | undefined;
}

const CreatePromotionModal: React.FC<CreatePromotionModalProps> = ({
  promotionVisibilityAndItems,
  initiallySelectedItems,
  onDone,
  onCancel,
}) => {
  const currentStore = useSelector(getCurrentStore);

  const [image_modal, setImageModal] = useState(false);
  const [selected_image, setSelectedImage] = useState<ImageType>();
  const [promotion, setPromotion] = useState<PromotionCreation>({
    name_en: '',
    name_ar: '',
    discount_type: 'flat',
    discount_value: '',
    max_discount_amount: null,
    min_order_value: null,
    start_datetime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
    end_datetime: dayjs().add(180, 'day').format('YYYY-MM-DD HH:mm:ss'),
    item_ids: initiallySelectedItems?.map((item) => item.id),
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [itemDiscounts, setItemDiscounts] = useState<
    { itemId: number; originalPrice: string; discountedPrice: number | null }[]
  >([]);

  useEffect(() => {
    if (promotionVisibilityAndItems.items.length > 1) {
      const initialDiscounts = promotionVisibilityAndItems.items.map(
        (itemId) => ({
          itemId: itemId.id,
          originalPrice: itemId.original_price,
          discountedPrice: null,
        }),
      );
      setItemDiscounts(initialDiscounts);
    }
  }, [promotionVisibilityAndItems.items]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPromotion((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemDiscountChange = (
    itemId: number,
    discountedPrice: number,
  ) => {
    setItemDiscounts((prev) =>
      prev.map((item) =>
        item.itemId === itemId ? { ...item, discountedPrice } : item,
      ),
    );
  };

  const handleValidation = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!promotion.discount_type)
      newErrors.discount_type = 'Please select a discount type.';

    // If multiple items, validate each discounted price
    if (promotionVisibilityAndItems.items.length > 1) {
      itemDiscounts.forEach((item, _) => {
        if (
          item.discountedPrice === null ||
          isNaN(item.discountedPrice) ||
          item.discountedPrice < 0
        ) {
          newErrors[`discountedPrice_${item.itemId}`] =
            'Discounted price is required and must be a positive number.';
        } else if (item.discountedPrice > parseFloat(item.originalPrice)) {
          newErrors[`discountedPrice_${item.itemId}`] =
            'Discounted price cannot be greater than original price.';
        }
      });
    } else {
      if (!promotion.discount_value) {
        newErrors.discount_value =
          'Discount value is required.' +
          (promotion.discount_type === 'percentage'
            ? ' Percentage discount value must be between 0 and 100.'
            : ' Discount value must be a positive number.');
      } else if (
        promotion.discount_type === 'percentage' &&
        (parseFloat(promotion.discount_value) < 0 ||
          parseFloat(promotion.discount_value) > 100)
      ) {
        newErrors.discount_value =
          'Percentage discount value must be between 0 and 100.';
      } else if (
        promotion.discount_type === 'flat' &&
        parseFloat(promotion.discount_value) < 0
      ) {
        newErrors.discount_value =
          'Fixed discount value must be a positive number.';
      }
    }
    if (!promotion.end_datetime) {
      newErrors.end_datetime = 'End date is required.';
    } else if (dayjs(promotion.end_datetime).isBefore(dayjs())) {
      newErrors.end_datetime = 'End date must be in the future.';
    } else if (
      promotion.start_datetime &&
      dayjs(promotion.end_datetime).isBefore(dayjs(promotion.start_datetime))
    ) {
      newErrors.end_datetime = 'End date must be after start date.';
    }
    if (promotion.min_order_value && promotion.min_order_value < 0) {
      newErrors.min_order_value = 'Minimum order value must be positive.';
    }
    if (promotion.max_discount_amount && promotion.max_discount_amount < 0) {
      newErrors.max_discount_amount =
        'Maximum discount value must be positive.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (handleValidation()) {
      // Create promotions for each item, either one by one or together
      const itemDiscountPayload = itemDiscounts.map((item) => ({
        item_id: item.itemId,
        discount_value:
          parseFloat(item.originalPrice) - (item.discountedPrice || 0),
      }));
      // If multiple items, create promotion for each item separately
      if (promotionVisibilityAndItems.items.length > 1) {
        // Convert end and start datetime to UTC
        const startDate = moment(promotion.start_datetime)
          .utc()
          .format('YYYY-MM-DD HH:mm:ss');
        const endDate = moment(promotion.end_datetime)
          .utc()
          .format('YYYY-MM-DD HH:mm:ss');
        await Promise.all(
          itemDiscountPayload.map(async (discount) => {
            await PromotionsRepository.createPromotion({
              store_id: currentStore.id,
              item_ids: [discount.item_id],
              name_en: promotion.name_en,
              name_ar: promotion.name_ar,
              img_id: selected_image?.id,
              discount_type: 'flat',
              discount_value: discount.discount_value,
              min_order_value: promotion.min_order_value!,
              start_datetime: startDate,
              end_datetime: endDate,
            });
          }),
        );
      } else {
        // Single promotion for one or all items
        await PromotionsRepository.createPromotion({
          store_id: currentStore.id,
          item_ids: promotionVisibilityAndItems.items.map((item) => item.id),
          name_en: promotion.name_en,
          name_ar: promotion.name_ar,
          img_id: selected_image?.id,
          discount_type: promotion.discount_type!,
          discount_value:
            promotion.discount_type === 'flat'
              ? parseFloat(promotion.discount_value)
              : parseFloat(promotion.discount_value) / 100,
          min_order_value: promotion.min_order_value!,
          start_datetime: promotion.start_datetime!,
          end_datetime: promotion.end_datetime!,
        });
      }
      onDone && onDone();
    } else {
      toast.warn(
        'Please fix the errors in the form.\n' +
          Object.values(errors).join('\n'),
      );
    }
  };

  const sectionTitleClass = 'text-m font-bold mb-2';

  return (
    <Modal
      style={{ top: 20 }}
      open={promotionVisibilityAndItems.show}
      footer={false}
      onCancel={() => onCancel?.()}
      width={700}
      destroyOnClose={true}
    >
      <ImageSelection
        s3Repository={S3ApiRepository.getInstance(managementAxiosInstance)}
        filesRepository={SellerFilesRepository}
        showModal={image_modal}
        updateSelectedImages={(images) => {
          setSelectedImage(images[0]);
          setPromotion((prev) => ({
            ...prev,
            img_id: images[0].id,
          }));
        }}
        setImageModal={setImageModal}
      />
      <h5 className="modal-text font-bold text-lg">Promotion Creation</h5>
      <Spacer y={1} />
      <p className="modal-text">
        Would you like to create a discount for the item(s) you just created?
      </p>
      <Spacer y={5} />
      <h6 className={sectionTitleClass}>Image</h6>
      <div
        style={{
          position: 'relative',
          width: '80%',
          margin: '0 auto',
          padding: ' 0px 0px',
        }}
        onClick={() => {
          setImageModal(true);
        }}
      >
        {selected_image ? (
          <ShowImage
            image={selected_image}
            style={{
              width: '100%',
              height: '100%',
              aspectRatio: '16/9',
            }}
          />
        ) : (
          <img
            src={storeCoverPlaceholder_path}
            alt="coupon-img"
            className="image"
            onClick={() => {
              setImageModal(true);
            }}
          />
        )}
      </div>
      <Spacer y={3} />
      <div className="flex">
        <Input
          label="Name (English)"
          placeholder="Summer Sale"
          name="name_en"
          value={promotion.name_en}
          onChange={handleChange}
          errorMessage={errors.name_en}
          required
          color="success"
          variant="bordered"
        />
        <Spacer x={3} />
        <Input
          label="Name (عربي)"
          placeholder="تخفيضات الصيف"
          name="name_ar"
          value={promotion.name_ar}
          onChange={handleChange}
          errorMessage={errors.name_ar}
          required
          color="success"
          variant="bordered"
        />
      </div>
      <Spacer y={4} />
      {promotionVisibilityAndItems.items.length === 1 && (
        <>
          <h6 className={sectionTitleClass}>Discount Type</h6>
          <div className="flex flex-row gap-1 align-middle items-center">
            <Chip
              className="mr-2"
              variant="bordered"
              color={promotion.discount_type === 'flat' ? 'success' : 'default'}
              onClick={() => {
                setPromotion((prev) => ({
                  ...prev,
                  discount_type: 'flat',
                  max_discount_amount: null,
                }));
              }}
            >
              Fixed Amount
            </Chip>
            <Chip
              color={
                promotion.discount_type === 'percentage' ? 'success' : 'default'
              }
              variant="bordered"
              onClick={() => {
                setPromotion((prev) => ({
                  ...prev,
                  discount_type: 'percentage',
                  max_discount_amount: null,
                }));
              }}
            >
              Percentage
            </Chip>
            <Spacer y={3} />
            <Input
              label="Discount Value"
              name="discount_value"
              value={promotion.discount_value}
              onChange={handleChange}
              type="number"
              errorMessage={errors.discount_value}
              required
              color="success"
              validate={
                promotion.discount_type === 'percentage'
                  ? (value) => {
                      if (parseFloat(value) < 0 || parseFloat(value) > 100) {
                        return 'Percentage discount value must be between 0 and 100.';
                      }
                    }
                  : (value) => {
                      if (parseFloat(value) < 0) {
                        return 'Discount value must be a positive number.';
                      }
                    }
              }
              startContent={
                promotion.discount_type === 'percentage'
                  ? '%'
                  : CURRENT_CURRENCY
              }
              placeholder={'10'}
              variant="bordered"
            />
          </div>
          <Spacer y={3} />
        </>
      )}
      <h6 className={sectionTitleClass}>Restrictions</h6>
      <Spacer y={3} />
      <DateRangePicker
        label="Promotion Period"
        variant="bordered"
        color="success"
        showMonthAndYearPickers
        defaultValue={{
          start: parseAbsoluteToLocal(dayjs().toISOString()) as any,
          end: parseAbsoluteToLocal(
            dayjs().add(180, 'day').toISOString(),
          ) as any,
        }}
        isDateUnavailable={(date) => {
          // Disable all past dates (before today)
          // NextUI DateValue: compare to today using .toDate()
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const dateObj = date?.toDate?.(ISRAEL_TIMEZONE);
          return dateObj ? dateObj < today : false;
        }}
        onChange={(range) => {
          setPromotion((prev) => ({
            ...prev,
            start_datetime: dayjs((range as any)?.start?.toDate?.()).format(
              'YYYY-MM-DD HH:mm:ss',
            ),
            end_datetime: dayjs((range as any)?.end?.toDate?.()).format(
              'YYYY-MM-DD HH:mm:ss',
            ),
          }));
        }}
        errorMessage={errors.start_datetime}
      />
      <Spacer y={3} />
      <div className="flex">
        <Input
          label="Minimum Order Value"
          startContent={CURRENT_CURRENCY}
          validate={(value) => {
            if (parseFloat(value) < 0) {
              return 'Minimum order value must be a positive number.';
            }
          }}
          name="min_order_value"
          placeholder="0"
          color="success"
          variant="bordered"
          value={promotion.min_order_value?.toString()}
          onChange={handleChange}
          type="number"
        />
        <Spacer x={3} />
        <Input
          label="Maximum Discount Value"
          startContent={CURRENT_CURRENCY}
          validate={(value) => {
            if (parseFloat(value) < 0) {
              return 'Maximum discount value must be a positive number.';
            }
          }}
          name="max_discount_amount"
          placeholder="0"
          defaultValue="0"
          color="success"
          variant="bordered"
          value={promotion.max_discount_amount?.toString()}
          onChange={handleChange}
          type="number"
          isDisabled={promotion.discount_type === 'flat'}
        />
      </div>
      <Spacer y={3} />
      {promotionVisibilityAndItems.items.length > 1 && (
        <Table dataSource={itemDiscounts} rowKey="itemId" pagination={false}>
          <Table.Column title="ID" dataIndex="itemId" key="itemId" />
          <Table.Column
            title="Attributes"
            dataIndex="attributes"
            key="attributes"
            render={(_, record: { itemId: number }) => {
              const item = promotionVisibilityAndItems.items.find(
                (item) => item.id === record.itemId,
              );
              return item?.attributes.map((attribute) => (
                <div key={attribute.attr_id}>
                  {attribute.attr_name_en}: {attribute.attr_value_en} |
                </div>
              ));
            }}
          />
          <Table.Column
            title="Image"
            dataIndex="image"
            key="image"
            render={(_, record: { itemId: number }) => {
              const item = promotionVisibilityAndItems.items.find(
                (item) => item.id === record.itemId,
              );
              return item ? (
                <ShowImage
                  image={item.image}
                  style={{
                    width: '100%',
                    height: '100%',
                    aspectRatio: '16/9',
                  }}
                />
              ) : (
                <div>No Image</div>
              );
            }}
          />
          <Table.Column
            title="Price"
            dataIndex="originalPrice"
            key="originalPrice"
            render={(x) => <>{formatPrice(x)}</>}
          />
          <Table.Column
            title="Discounted Price"
            key="discountedPrice"
            render={(
              _,
              record: { itemId: number; discountedPrice: number | null },
            ) => (
              <Input
                type="number"
                value={record.discountedPrice?.toString() || ''}
                onChange={(e) =>
                  handleItemDiscountChange(
                    record.itemId,
                    parseFloat(e.target.value),
                  )
                }
              />
            )}
          />
        </Table>
      )}
      <Spacer y={3} />
      <div className="flex justify-between">
        <Button
          variant="flat"
          color="default"
          onPress={() => {
            onCancel && onCancel();
          }}
        >
          Not now
        </Button>
        <Button color="success" onPress={handleSubmit}>
          Create Promotion
        </Button>
      </div>
    </Modal>
  );
};

export default CreatePromotionModal;
